/*
 * Copyright 2026 Jason Figge
 *
 * This file is part of Chip Hippo.
 *
 * Chip Hippo is free software: you can redistribute it and/or modify it under
 * the terms of the GNU General Public License as published by the Free
 * Software Foundation, either version 3 of the License, or (at your option)
 * any later version.
 *
 * Chip Hippo is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for
 * more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with Chip Hippo. If not, see <https://www.gnu.org/licenses/>.
 */

// updater-monitor.js — turns the auto-updater's `chiphippo:updater-*`
// broadcasts into toasts (Feature 280). The always-on surface, alive for the
// whole session;
// Settings ▸ About owns the inline status line, which only exists while that
// dialog is open. Same shape as net-name-monitor.js: it listens to global
// events and pushes to the shared NotificationStack, and owns nothing else.
//
// WHAT IT DOES NOT SAY IS THE POINT. A STARTUP check is silent unless it found
// something — "you're up to date" is an answer to a question the user asked,
// and unasked it is a nag; an error nobody could act on is worse. So the
// up-to-date and failure toasts fire only when `manual` is set, which is main's
// record of whether a human pressed the button. There is deliberately no
// download-progress toast either: milestones are what a corner of the desk can
// usefully carry.

/** The one key every updater toast uses — see UpdaterMonitor#show. */
import { t } from "../i18n.js";

const TOAST_KEY = "updater";

export class UpdaterMonitor {
  #notifications;

  /**
   * @param {import('./notification-stack.js').NotificationStack} notifications
   */
  constructor(notifications) {
    this.#notifications = notifications;

    window.addEventListener("chiphippo:updater-available", (e) => {
      const version = e.detail?.version;
      this.#show({
        variant: "info",
        title: t("updater.available"),
        message: version
          ? t("updater.downloadingVersion", { version })
          : t("updater.downloading"),
      });
    });

    // The one toast that OFFERS something, so it carries the Restart button —
    // and it is sticky, because an update that finished downloading while the
    // user was wiring something must still be there when they look up. It
    // needs no deadline either way: declining costs nothing, since an
    // installed-on-quit update lands the next time the app is closed.
    window.addEventListener("chiphippo:updater-downloaded", (e) => {
      const version = e.detail?.version;
      this.#show({
        variant: "info",
        sticky: true,
        title: t("updater.ready"),
        message: version
          ? t("updater.downloadedVersion", { version })
          : t("updater.downloaded"),
        actionLabel: t("updater.restart"),
        onAction: () => window.chiphippo?.updater?.install?.(),
      });
    });

    window.addEventListener("chiphippo:updater-not-available", (e) => {
      // A dev or store build reports itself in Settings ▸ About, where the
      // answer makes sense beside the version it is about; a toast over the
      // desk saying "not from here" explains nothing — hence `reason`, which
      // is present only for those two.
      if (!e.detail?.manual || e.detail?.reason) return;
      this.#show({
        variant: "info",
        title: t("updater.upToDate"),
        message: t("settings.about.upToDate"),
      });
    });

    // Restart pressed before the update was ready to install (macOS, while
    // Squirrel takes it): nothing happens for a while, so say why — the
    // question about unsaved work, and the restart, come when it is. It takes
    // the "ready" toast's place, so it keeps that toast's terms: sticky (the
    // wait can run minutes) and still offering Restart — pressing it again
    // joins the wait, and after a Cancel on the question it is the offer.
    window.addEventListener("chiphippo:updater-preparing", () => {
      this.#show({
        variant: "info",
        sticky: true,
        title: t("updater.preparing"),
        message: t("updater.preparingMessage"),
        actionLabel: t("updater.restart"),
        onAction: () => window.chiphippo?.updater?.install?.(),
      });
    });

    // A Restart that did not restart: said, because the window simply staying
    // put explains nothing — and the offer stays, Restart and all, since the
    // update is still downloaded (a quit installs it only if Squirrel has it,
    // so that is not promised).
    // `blocked`: macOS's installer has failed this download (refused it, or
    // never got it ready) — Restart cannot help until it is fetched again, so
    // the offer is Check for Updates, which does that.
    window.addEventListener("chiphippo:updater-install-failed", (e) => {
      const blocked = e.detail?.blocked === true;
      this.#show({
        variant: "warning",
        sticky: true,
        title: t("updater.installFailed"),
        message: blocked
          ? t("updater.installBlockedMessage")
          : t("updater.installFailedMessage"),
        actionLabel: blocked ? t("settings.about.check") : t("updater.restart"),
        onAction: blocked
          ? () => window.chiphippo?.updater?.check?.()
          : () => window.chiphippo?.updater?.install?.(),
      });
    });

    window.addEventListener("chiphippo:updater-error", (e) => {
      if (!e.detail?.manual) return;
      this.#show({
        variant: "warning",
        title: t("updater.checkFailed"),
        message: t("settings.about.error"),
      });
    });
  }

  /**
   * Show a toast that REPLACES whatever the updater last said.
   *
   * An update announces itself in stages — downloading, then ready — and they
   * are one running commentary, not four independent warnings, so they must
   * not stack up in the corner. One shared key does that, but re-notifying a
   * live key rewrites only its WORDS — the "ready" stage would arrive without
   * its Restart button, or keep a sticky/variant it no longer means. So the
   * old one is dismissed first: same slot, the whole new toast.
   */
  #show(opts) {
    this.#notifications.dismiss(TOAST_KEY);
    this.#notifications.notify({ key: TOAST_KEY, ...opts });
  }
}
